// ==========================================================================
// Auth Service - OTP Authentication API calls
// Endpoints match: POST /api/auth/otp/send, POST /api/auth/otp/verify
// ==========================================================================

import api from '@/lib/axios';
import type {
  OTPSendRequest,
  OTPSendResponse,
  OTPSendRateLimitResponse,
  OTPVerifyRequest,
  OTPVerifyResponse,
} from '@/types/auth.types';
import { clearGeneratedLinkToken, getGeneratedLinkToken } from '@/lib/generatedLinkStorage';

/**
 * Send OTP to phone number
 * POST /api/auth/otp/send
 */
export async function sendOtp(data: OTPSendRequest): Promise<OTPSendResponse> {
  const response = await api.post<OTPSendResponse>('/auth/otp/send', {
    phone_number: data.phone_number,
    channel: data.channel || 'sms',
  });
  return response.data;
}

/**
 * Verify OTP code and get access token
 * POST /api/auth/otp/verify
 */
export async function verifyOtp(data: OTPVerifyRequest): Promise<OTPVerifyResponse> {
  const generatedLinkToken = data.generated_link_token ?? getGeneratedLinkToken() ?? undefined;

  const response = await api.post<OTPVerifyResponse>('/auth/otp/verify', {
    phone_number: data.phone_number,
    code: data.code,
    device_name: data.device_name || 'mobile',
    ...(generatedLinkToken ? { generated_link_token: generatedLinkToken } : {}),
  });

  // The slug is single-use from the PWA's perspective — once it's been sent
  // on a successful verify, drop it so a later re-auth doesn't replay an
  // already-converted click.
  if (generatedLinkToken) {
    clearGeneratedLinkToken();
  }

  return response.data;
}

/**
 * Resend OTP (same endpoint as send)
 * POST /api/auth/otp/send
 */
export async function resendOtp(data: OTPSendRequest): Promise<OTPSendResponse | OTPSendRateLimitResponse> {
  const response = await api.post('/auth/otp/send', {
    phone_number: data.phone_number,
    channel: data.channel || 'sms',
  });
  return response.data;
}
